import {
  Button,
  DataTable,
  Dialog,
  Note,
  ProgressSpinner
} from "@appretio/ui-kit";
import { useMemo } from "react";
import { useFetchAggLevelStatus } from "../../hooks/useFetchAggLevelStatus";
import * as styles from "./CubeOperationModal.css";
import { cubeOperationColumnDefs } from "./cubeOperationModalColumns";

interface CubeOperationModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Called when the modal should be closed
   */
  onClose: () => void;
  /**
   * Selected cube ID for mismatch display
   */
  selectedCubeId: string;
}

export function CubeOperationModal({
  isOpen,
  onClose,
  selectedCubeId
}: CubeOperationModalProps) {
  // Fetch aggregation level status data for the selected cube only when modal is open.
  const {
    data: aggLevelStatusData,
    isLoading,
    isFetching,
    isError,
    refetch
  } = useFetchAggLevelStatus(selectedCubeId, isOpen && !!selectedCubeId);

  const showLoading = isLoading || (isFetching && !aggLevelStatusData);

  const statusCounts = useMemo(() => {
    if (!aggLevelStatusData) {
      return { new: 0, abandoned: 0, newBusiness: 0 };
    }

    return aggLevelStatusData.reduce(
      (acc, item) => {
        if (item.agg_level_status === "NEW") {
          acc.new++;
        } else if (item.agg_level_status === "ABANDONED") {
          acc.abandoned++;
        } else if (item.agg_level_status === "NEW_BUSINESS") {
          acc.newBusiness++;
        }
        return acc;
      },
      { new: 0, abandoned: 0, newBusiness: 0 }
    );
  }, [aggLevelStatusData]);

  const noteText = useMemo(() => {
    if (showLoading) {
      return "Aggregation level mismatch analysis in progress. Please wait.";
    }

    const {
      new: newCount,
      abandoned: abandonedCount,
      newBusiness: newBusinessCount
    } = statusCounts;
    const totalMismatches = newCount + abandonedCount + newBusinessCount;

    if (totalMismatches === 0) {
      return "Your cube aggregation levels match master data. No mismatches detected.";
    }

    const parts = [];

    if (newCount > 0) {
      parts.push(
        `${newCount} new (i.e. present in master data but not in the cube) level(s) detected`
      );
    }

    if (abandonedCount > 0) {
      parts.push(
        `${abandonedCount} abandoned (i.e. present in cube but not in master data) level(s) detected`
      );
    }

    if (newBusinessCount > 0) {
      parts.push(`${newBusinessCount} new business level(s) detected`);
    }

    return `Your cube aggregation levels do not match master data. ${parts.join(". ")}. It is recommended to correct the mismatch before proceeding. New levels are highlighted in green, abandoned levels are indicated in red, while new business levels are shown in amber.`;
  }, [showLoading, statusCounts]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      title="Cube aggregation level mismatch"
      size="auto"
      modal={true}
      hideCloseIcon={false}
      // An error surface, not a confirmation: this is where "something
      // broke" reports about the mismatch itself come from.
      feedbackName="cube-operation-error"
      feedbackContext={() => ({ status_counts: statusCounts })}
    >
      <div className={styles.contentContainer}>
        <div>
          {isError ? (
            <>
              <Note
                className={styles.cubeNote}
                variant="error"
                text="Failed to load aggregation level status. The cube state is unknown — retry to check again."
              />
              <Button variant="secondary" onClick={() => refetch()}>
                Retry
              </Button>
            </>
          ) : (
            <>
              <Note className={styles.cubeNote} icon="info" text={noteText} />
              <div className={styles.tableContainer}>
                {showLoading ? (
                  <div className={styles.spinnerContainer}>
                    <ProgressSpinner />
                  </div>
                ) : (
                  <DataTable
                    columnDefs={cubeOperationColumnDefs}
                    rowData={aggLevelStatusData ?? []}
                    rowHeight={80}
                    loading={false}
                    gridOptions={{}}
                    noBorders
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}
